import { Button, InputWrapper } from '@vrplatform/components';
import { Box, PinInput, Text, rem } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import type { SecondFactorSubmission } from '../../hooks/useSignInForm.utils';

type SecondFactorViewProps = {
  returnToDefault: () => void;
  submitSecondFactor: (input: SecondFactorSubmission) => Promise<void>;
};

export const SecondFactorView = ({
  submitSecondFactor,
  returnToDefault,
}: SecondFactorViewProps) => {
  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
    watch,
    reset,
  } = useForm<SecondFactorSubmission>({
    values: {
      code: '',
      strategy: 'totp',
    },
  });

  const strategy = watch('strategy');

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(submitSecondFactor)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
      onReset={() => {
        if (strategy === 'backup_code') {
          reset({
            code: '',
            strategy: 'totp',
          });
        } else {
          returnToDefault();
        }
      }}
    >
      <Text component="p" ta="center" mt="7vh" mb="3rem">
        {strategy === 'totp' ? (
          'Please enter your 2FA code. It is time-based authentication code from a service such as Google Authenticator.'
        ) : (
          <>
            A backup code is one time usage code that you can use to access your
            account in case you lose your 2FA device.
            <br />
            <br />
            Once logged in, please visit your profile settings to update your
            2FA.
          </>
        )}
      </Text>
      <Controller
        name="code"
        defaultValue=""
        control={control}
        rules={{
          required: {
            value: true,
            message: 'Please enter your 2FA code.',
          },
        }}
        render={({
          field: { value, onChange, name },
          fieldState: { error },
        }) => (
          <InputWrapper
            label={strategy === 'totp' ? 'Code' : 'Backup Code'}
            error={error?.message}
            sx={{
              marginBottom: rem(50),
            }}
          >
            <PinInput
              data-testid="2fa-pin-input"
              value={value}
              onChange={onChange}
              name={name}
              length={strategy === 'totp' ? 6 : 8}
              size="lg"
            />
          </InputWrapper>
        )}
      />

      <Button
        variant="primary"
        loading={isSubmitting}
        type="submit"
        sx={(theme) => ({
          width: '100%',
          maxWidth: 400,
          marginBottom: theme.spacing.md,
        })}
      >
        {strategy === 'totp' ? 'Confirm code' : 'Submit backup code'}
      </Button>

      <Button
        disabled={isSubmitting}
        type="reset"
        sx={(theme) => ({
          width: '100%',
          maxWidth: 400,
          marginBottom: theme.spacing.md,
        })}
      >
        {strategy === 'backup_code'
          ? 'Return to enter 2FA code'
          : 'Return to login'}
      </Button>

      {strategy === 'totp' && (
        <Text c="gray" ta="center" mb="xl" mt="xl">
          Have you lost your authentication device, but still have your backup
          codes?
          <br />
          <br />
          <Text
            type="button"
            onClick={() => {
              reset({
                code: '',
                strategy: 'backup_code',
              });
            }}
            sx={(theme) => ({
              color: theme.colors[theme.primaryColor][6],
              textDecoration: 'underline',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              border: 'none',
              ':active': {
                transform: 'translateY(1px)',
              },
            })}
            component="button"
          >
            Click here
          </Text>{' '}
          to enter a backup code instead.
        </Text>
      )}
    </Box>
  );
};
